import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Button from '../../library/button/button';
import { Section } from '../../library/page/page';
import ReviewOverview from '../review-overview/review-overview';
import Reviews, { Review } from '../reviews/reviews';
import VerifiedReviewsMessage from '../verified-reviews-message/verified-reviews-message';

import { SUBMIT_REVIEW_FEEDBACK } from '../../shared/graphql/review-mutations';

import { useMutation } from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { localStorage as ls } from '../../shared/utils/storage/storage';

import styles from './product-reviews.scss';

const reviewAnswerLabels = {
  corneringsteering: 'Cornering/Steering',
  drytraction: 'Dry Traction',
  ridecomfort: 'Ride Comfort',
  ridenoise: 'Ride Noise',
  treadlife: 'Tread Life',
  wettraction: 'Wet Traction'
};

const reviewInfoLabels = {
  drivingconditions: 'Driving Conditions',
  milesdriven: 'Miles Driven On Tires',
  typeofdriving: 'Driving Style'
};

const averageRatingLabels = {
  corneringsteering: 'Cornering/Steering',
  drytraction: 'Dry Traction',
  ridecomfort: 'Ride Comfort',
  ridenoise: 'Ride Noise',
  treadlife: 'Tread Life',
  wettraction: 'Wet Traction'
};

const ProductReviews = React.forwardRef(function ProductReviews(props, ref) {
  const {
    averageRatings,
    hasProductLineReviews,
    includeVariantProductReviews,
    productReviews,
    reviewSummary,
    reviews,
    setIncludeVariantProductReviews,
    setPageNumber,
    setReviewsFilterSortQuery
  } = props;

  const { mutate: submitFeedback } = useMutation(SUBMIT_REVIEW_FEEDBACK);
  const { facets, pagination, sorts } = reviews || {};
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedSort, setSelectedSort] = useState(null);
  const [feedbackIds, setFeedbackIds] = useState(getReviewFeedbackIds);

  function getReviewFeedbackIds() {
    return ls('productReviews.feedbackIds') || [];
  }

  function sortSortOptions(a) {
    if (a.code === 'most-recent-sr') return -1;
    return 1;
  }

  function getSortOptions() {
    let sortOptions = [];

    if (Array.isArray(sorts)) {
      sortOptions = sorts.sort(sortSortOptions).map(o => {
        const label =
          typeof o?.code === 'string'
            ? o.code
                .replace('-tr', '')
                .split('-')
                .map(w => w.charAt(0).toUpperCase() + w.substring(1))
                .join(' ')
            : '';

        return {
          label,
          value: o?.code
        };
      });
    }

    return sortOptions;
  }

  function getFilters() {
    let filters = [];
    const filterTypeLabels = {
      drivingconditions: 'Conditions',
      milesdrivenRange: 'Miles Driven',
      ratingRange: 'Rating',
      typeofdriving: 'Driving Style'
    };

    if (Array.isArray(facets)) {
      filters = facets
        .filter(f => f.code in filterTypeLabels)
        .map(f => ({
          code: f.code,
          label: filterTypeLabels[f.code],
          options: [
            { label: 'Any', value: '' },
            ...(f?.values?.map(v => ({
              label: v?.name,
              value: `:${f.code}:${v?.name}`
            })) || [])
          ]
        }));
    }

    return filters;
  }

  function getAverageCustomerRatings() {
    let averageCustomerRatings = null;

    if (Array.isArray(averageRatings) && averageRatings.length > 0) {
      averageCustomerRatings = averageRatings
        .filter(r => averageRatingLabels[r?.key])
        .map(r => ({
          code: r.key,
          label: averageRatingLabels[r.key] || '',
          value: parseFloat(r.value)
        }));
    }

    return averageCustomerRatings;
  }

  function getSummaryRatingCounts() {
    let ratingCounts = null;

    if (Array.isArray(facets)) {
      const ratings = facets.find(f => f.code === 'rating') || {};
      const { values = [] } = ratings;
      const counts = values.reduce((acc, v) => {
        if (v?.name && v?.count) {
          acc[v.name] = v.count;
        }
        return acc;
      }, {});

      ratingCounts = ['5', '4', '3', '2', '1'].map(r => ({
        count: counts[r] || 0,
        rating: r
      }));
    }

    return ratingCounts;
  }

  function submitReviewFeedback(reviewId, isHelpful) {
    submitFeedback({
      isHelpful,
      reviewId
    });

    if (!feedbackIds.includes(reviewId)) {
      const ids = [...feedbackIds, reviewId];

      setFeedbackIds(ids);
      ls('productReviews.feedbackIds', ids);
    }

    trackAction(`product_review_review_rate_${isHelpful ? '' : 'un'}helpful`);
  }

  function updateSelection(sortOption, filterOptions) {
    let query = '';
    const sort = sortOption ? sortOption : selectedSort;
    const filters = filterOptions ? filterOptions : selectedFilters;

    if (sort?.value) query += `:${sort?.value}`;

    if (typeof filters === 'object') {
      for (const key in filters) {
        const filterValue = filters[key]?.value;
        if (filterValue) {
          query += filterValue;
        }
      }
    }

    setSelectedFilters(filters);
    setSelectedSort(sort);
    setPageNumber(0);
    setReviewsFilterSortQuery(query);
  }

  function onFilterChange(filterOption, filterGroup) {
    if (filterOption && filterGroup) {
      updateSelection(null, {
        ...selectedFilters,
        [filterGroup?.code]: filterOption
      });
    }
  }

  function onSortChange(sortOption) {
    if (sortOption) {
      updateSelection(sortOption);
    }
  }

  function onLoadMore() {
    setPageNumber(page => page + 1);
  }

  const filters = useMemo(getFilters, [facets]);
  const sortOptions = useMemo(getSortOptions, [sorts]);
  const summaryRatingCounts = useMemo(getSummaryRatingCounts, [facets]);
  const averageAnswerRatings = useMemo(getAverageCustomerRatings, [
    averageRatings
  ]);
  let showLoadMore = false;

  if (
    pagination &&
    typeof pagination.currentPage === 'number' &&
    typeof pagination.numberOfPages === 'number' &&
    pagination.currentPage + 1 < pagination.numberOfPages
  ) {
    showLoadMore = true;
  }

  useEffect(() => {
    if (Array.isArray(sortOptions) && sortOptions.length > 1) {
      const sort = selectedSort
        ? selectedSort
        : sortOptions.find(s => s.label === 'Most Recent');
      setSelectedSort(sort);
    }
  }, [sortOptions, selectedSort]);

  useEffect(() => {
    trackAction('product_reviews');
  }, []);

  return (
    <Section
      className={styles['container']}
      intro={
        productReviews?.length > 0 ? (
          <VerifiedReviewsMessage />
        ) : (
          'There are currently no reviews for this product.'
        )
      }
      ref={ref}
    >
      {reviewSummary && (
        <ReviewOverview
          averageAnswerRatings={averageAnswerRatings}
          className="overview"
          headingTag="h3"
          numberOfReviews={reviewSummary?.totalCount}
          ratingCounts={summaryRatingCounts}
          recommendedCount={reviewSummary?.recommendCount}
          recommendedMessage="people recommend these tires"
          totalRating={
            reviewSummary?.totalRating > 0 ? reviewSummary?.totalRating : ''
          }
        />
      )}
      {hasProductLineReviews && (
        <Button
          className={styles['review-tire-button']}
          onClick={() => {
            setIncludeVariantProductReviews(!includeVariantProductReviews);
            setPageNumber(0);
          }}
        >
          {includeVariantProductReviews
            ? 'see reviews for your tire size'
            : 'see all reviews'}
        </Button>
      )}
      {Array.isArray(productReviews) && productReviews.length > 0 && (
        <Reviews
          filters={filters}
          onFilterChange={onFilterChange}
          onLoadMore={onLoadMore}
          onResetFilters={() => updateSelection(null, {})}
          onSortChange={onSortChange}
          selectedFilters={selectedFilters}
          selectedSort={selectedSort}
          showLoadMore={showLoadMore}
          sortOptions={sortOptions}
        >
          {productReviews.map((review, idx) => {
            const hasSubmittedFeedback = feedbackIds.includes(review.id);
            let [yearMake, modelTrim] = review.vehicleDetails
              ? review.vehicleDetails.split(' | ')
              : [];
            const answers = [];
            const details = [];
            let state = null;
            let city = null;

            if (modelTrim) {
              modelTrim = modelTrim.replace(/\s\/.+/g, '');
            }

            if (Array.isArray(review.answers)) {
              let i;
              for (i = 0; i < review.answers.length; i++) {
                const answer = review.answers[i];
                const { key, value } = answer || {};

                if (key === 'state') {
                  state = value;
                }

                if (key === 'city') {
                  city = value;
                }

                if (reviewInfoLabels[key]) {
                  details.push({
                    label: reviewInfoLabels[key],
                    value: value
                  });
                }

                if (reviewAnswerLabels[key]) {
                  answers.push({
                    label: reviewAnswerLabels[key],
                    rating: value
                  });
                }
              }
            }

            return (
              <Review
                answers={answers}
                city={city}
                comments={review.reviewText}
                date={review.submissionDate}
                details={details}
                heading={yearMake?.toUpperCase?.()}
                helpfulCount={review.helpfulCount}
                id={review.id}
                isFeedbackSubmitted={hasSubmittedFeedback}
                isRecommended={review.recommended}
                key={`sr-${idx}`}
                name={review.userDisplayName}
                notHelpfulCount={review.notHelpfulCount}
                notRecommendedMessage="No, I wouldn't purchase this tire again."
                onHelpfulClick={submitReviewFeedback}
                rating={review.averageRating}
                recommendedMessage="Yes, I would purchase this tire again."
                reviewerName={review?.userDisplayName}
                state={state}
                subHeading={modelTrim}
              />
            );
          })}
        </Reviews>
      )}
    </Section>
  );
});

ProductReviews.propTypes = {
  averageRatings: PropTypes.arrayOf(PropTypes.object),
  hasProductLineReviews: PropTypes.bool,
  includeVariantProductReviews: PropTypes.bool,
  productReviews: PropTypes.arrayOf(PropTypes.object),
  reviewSummary: PropTypes.object,
  reviews: PropTypes.arrayOf(PropTypes.object),
  setIncludeVariantProductReviews: PropTypes.func,
  setPageNumber: PropTypes.func,
  setReviewsFilterSortQuery: PropTypes.func
};

export default ProductReviews;
